import { Injectable } from '@angular/core';
import { ProductResource } from '@df/catalog/product/product.resource';
import compact from 'lodash-es/compact';
import { SeeRangeInStore } from './see-range-in-store';
import { SeeRangeInStoreResource } from './see-range-in-store.resource';
export interface ISeeRangeInStoreLoadModelsResponse {
    productModels: SeeRangeInStore[];
    rangeModels?: SeeRangeInStore[];
}
@Injectable({ providedIn: 'root' })
export class SeeRangeInStoreService {
    constructor(
        private seeRangeInStoreResource: SeeRangeInStoreResource,
        private productResource: ProductResource
    ) {}
    /**
     * Loads see range in store models for product
     * @param productId
     * @param storesIds
     * @param rangeId   if there is no seeRangeInStore for product, we will load data for other products from the range
     * @returns
     */
    loadSeeRangeInStoreModels(productId: number, storesIds: number[], rangeId?: number): Promise<ISeeRangeInStoreLoadModelsResponse> {
        return new Promise((resolve, reject) => {
            const toLoadIds = this.getToLoadIds(productId, storesIds);
            const finish = () => {
                const response: ISeeRangeInStoreLoadModelsResponse = { productModels: this.getSeeRangeInStoreModels(productId, storesIds) };
                if (rangeId) {
                    response.rangeModels = this.getSeeRangeInStoreRangeModels(rangeId, storesIds);
                }
                resolve(response);
            };
            if (toLoadIds.length) {
                return this.seeRangeInStoreResource
                    .find({ verbosity: 1, filter: { product: productId, storepickup: { $in: toLoadIds } } })
                    .then(() => {
                        if (rangeId) {
                            const toLoadRangeIds = this.getToLoadIds(productId, storesIds);
                            if (toLoadRangeIds.length) {
                                this.seeRangeInStoreResource
                                    .find({
                                        verbosity: 1,
                                        filter: { storepickup: { $in: toLoadRangeIds }, nyw_item_range_code_nh: rangeId }
                                    })
                                    .finally(() => finish());
                            } else {
                                finish();
                            }
                        } else {
                            finish();
                        }
                    })
                    .catch(() => reject());
            } else {
                finish();
            }
        });
    }
    loadSeeRangeInStoreModel(productId: number): Promise<SeeRangeInStore | undefined> {
        return new Promise((resolve, reject) => {
            const model = this.getSeeRangeInStoreModel(productId);
            if (model) {
                resolve(model);
            } else {
                this.seeRangeInStoreResource.find(
                    { verbosity: 1, filter: { product: productId } }).then(() => {
                        resolve(this.getSeeRangeInStoreModel(productId));
                    }).catch(()=> reject()
                );
            }
        });
    }
    getSeeRangeInStoreModels(productId: number, storeIds: number[]): SeeRangeInStore[] {
        return compact(storeIds.map(storeId => this.getSeeRangeInStoreModel(productId, storeId)));
    }
    getSeeRangeInStoreRangeModels(rangeId: number, storeIds: number[]): SeeRangeInStore[] {
        const allLoaded = this.seeRangeInStoreResource.getAllExisting();
        return allLoaded.filter(model => {
            return model.data?.nyw_item_range_code_nh === rangeId && model.storepickupId && storeIds.indexOf(model.storepickupId) > -1;
        });
    }
    getSeeRangeInStoreModel(productId: number, storeId?: number): SeeRangeInStore | undefined {
        const allLoaded = this.seeRangeInStoreResource.getAllExisting();
        return allLoaded.find(i => {
            if (i.productId === productId) {
                if (storeId) {
                    return i.storepickupId === storeId;
                }
                return true;
            }
            return false;
        });
    }
    private getToLoadIds(productId: number, storesIds: number[]): number[] {
        return storesIds.filter(storeId => !this.getSeeRangeInStoreModel(productId, storeId));
    }
}
